class CartNotificationPopup extends HTMLElement {
  constructor() {
    super();
    this.sectionId = 'cart-notification-popup-content';
    this.recommendationsSectionId = 'product-recommendations-inline';
    this.activeElement = null;
    this.selectedColorValue = '';

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-popup-close]')) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  get sync() {
    return window.ProductCardColorSync || {};
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-popup-content',
        section: this.sectionId,
        selector: '.shopify-section',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : html;
  }

  getColorOptionNames() {
    return (this.sync.parseColorOptionNames || (() => []))(this.dataset.colorOptionNames);
  }

  extractColorValue(parsedState) {
    const colorNames = this.getColorOptionNames();
    const item = parsedState?.items?.[0] || parsedState;
    const optionsWithValues = item?.options_with_values;

    if (Array.isArray(optionsWithValues)) {
      const match = optionsWithValues.find((option) => {
        const name = String(option?.name || '').toLowerCase();
        return colorNames.some((colorName) => name === colorName || name.includes(colorName));
      });
      if (match?.value) return String(match.value).trim();
    }

    // Fallback: read from rendered popup item HTML
    const optionNodes = this.querySelectorAll('.cart-notification-popup-item__option');
    for (const node of optionNodes) {
      const label = node.querySelector('.cart-notification-popup-item__option-label');
      const name = String(label?.textContent || '')
        .replace(/:$/, '')
        .trim()
        .toLowerCase();
      if (!colorNames.some((colorName) => name === colorName || name.includes(colorName))) continue;
      const text = String(node.textContent || '');
      const value = text.includes(':') ? text.split(':').slice(1).join(':').trim() : text.trim();
      if (value) return value;
    }

    return '';
  }

  applyMatchingColor(root, colorValue) {
    this.sync.applyMatchingColor?.(root, colorValue);
  }

  clearRecommendationNodes(footer) {
    footer
      ?.querySelectorAll('[data-product-recommendations-inline], .product-recommendations-inline-loader')
      .forEach((node) => node.remove());
  }

  setLoadingVisible(footer, visible) {
    const loading = footer?.querySelector('[data-cart-popup-loading]');
    if (!loading) return;
    loading.classList.toggle('is-hidden', !visible);
  }

  updateCartCount(html) {
    const cartCountEl = document.querySelector('[data-cart-count]');
    if (!cartCountEl) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const cartItems = parsed.querySelector('[data-cart-items]');
    if (!cartItems) return;

    const count = cartItems.dataset.cartItems;
    if (count == 0 || count == '0' || count == '') {
      cartCountEl.classList.add('hidden');
      cartCountEl.textContent = '';
    } else if (parseInt(count, 10) > 99) {
      cartCountEl.classList.remove('hidden');
      cartCountEl.classList.add('large-items');
      cartCountEl.textContent = '';
    } else {
      cartCountEl.classList.remove('hidden', 'large-items');
      cartCountEl.textContent = count;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  showLoadingState() {
    const footer = this.querySelector('[data-cart-notification-popup-footer]');
    if (!footer) return;
    this.clearRecommendationNodes(footer);
    this.setLoadingVisible(footer, true);
  }

  getRecommendationsUrl(parsedState) {
    const item = parsedState?.items?.[0] || parsedState;
    const productId = item?.product_id;
    const base = window.routes?.product_recommendations_url;
    if (!productId || !base) return '';

    const limit = this.dataset.recommendationsLimit || '6';
    const params = new URLSearchParams({
      section_id: this.recommendationsSectionId,
      product_id: String(productId),
      limit: String(limit),
      intent: 'complementary',
    });
    return `${base}?${params.toString()}`;
  }

  loadRecommendations(parsedState) {
    const footer = this.querySelector('[data-cart-notification-popup-footer]');
    if (!footer) return;

    const url = this.getRecommendationsUrl(parsedState);
    if (!url) {
      this.setLoadingVisible(footer, false);
      return;
    }

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const markup = this.sync.extractInlineRecommendations?.(html) || '';
        if (!markup) {
          this.setLoadingVisible(footer, false);
          return;
        }

        const wrapper = document.createElement('div');
        wrapper.innerHTML = markup;
        // Unwrap product-recommendations so theme IntersectionObserver does not re-fetch.
        const recEl = wrapper.querySelector('product-recommendations');
        const content = (recEl || wrapper).querySelector('[data-product-recommendations-inline]');
        if (!content) {
          this.setLoadingVisible(footer, false);
          return;
        }

        this.clearRecommendationNodes(footer);
        const loading = footer.querySelector('[data-cart-popup-loading]');
        footer.insertBefore(content, loading);
        this.setLoadingVisible(footer, false);

        requestAnimationFrame(() => {
          this.applyMatchingColor(content, this.selectedColorValue);
        });
      })
      .catch(() => {
        this.setLoadingVisible(footer, false);
      });
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.querySelector('.cart-notification-popup__close')?.focus();
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    if (this.activeElement) {
      this.activeElement.focus();
    }
  }

  renderContents(parsedState) {
    const sectionHtml = parsedState.sections?.[this.sectionId];
    if (!sectionHtml) {
      console.warn('[cart-notification-popup] Missing section HTML:', this.sectionId);
      return;
    }

    const content = this.querySelector('[data-cart-notification-popup]');
    const itemHtml = this.getSectionInnerHTML(sectionHtml);
    const doc = new DOMParser().parseFromString(itemHtml, 'text/html');
    const variantId = parsedState.variant_id || parsedState.id;
    const itemNode =
      doc.querySelector(`#CartNotificationPopupItem-${variantId}`) ||
      doc.querySelector('.cart-notification-popup-item');

    if (content) {
      content.innerHTML = itemNode ? itemNode.outerHTML : itemHtml;
    }

    this.selectedColorValue = this.extractColorValue(parsedState);
    this.updateCartCount(sectionHtml);
    this.showLoadingState();
    this.open();
    this.loadRecommendations(parsedState);
  }
}

if (!customElements.get('cart-notification-popup')) {
  customElements.define('cart-notification-popup', CartNotificationPopup);
}
